import React, { useMemo } from 'react';

import { Button, Skeleton, Typography } from '@mui/material';

import { jbColors } from '../../theme/jbColors';
import { Box } from '@mui/material';

type Props = {
  heading: string;
  isActive: boolean;
  filterKey: string;
  text: string;
  subText: string;
  loading: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>, filterKey: string) => void;
};

export const ProductStatusFilter = ({ text, subText, filterKey, isActive, loading, onClick }: Props) => {
  const handleClick = (e: React.MouseEvent<HTMLElement>, filterKey: string) => {
    onClick(e, filterKey);
  };
  const [hover, setHover] = React.useState(false);

  const styles = useMemo(() => {
    return {
      button: {
        minWidth: '4em', // Minimum width
        height: '2.5em', // Adjusted height
        backgroundColor: jbColors.core.stone[10],
        cursor: !subText ? 'auto' : 'pointer',
        '&:hover': {
          textDecoration: !subText ? 'none' : 'bold',
          backgroundColor: !subText ? jbColors.core.stone[20] : jbColors.core.stone[10],
          border: subText ? `1px solid ${jbColors.support.alertBlue[110]}` : `1px solid ${jbColors.core.stone[20]}`,
          color: subText ? `${jbColors.core.reflexBlue[100]} !important` : jbColors.core.stone[10],
        },
        pointerEvents: !subText ? 'none' : 'auto',
        color: isActive ? jbColors.core.reflexBlue[100] : jbColors.support.solidGrayIcon[10],
        whiteSpace: 'normal', // Allow text to wrap
        justifyContent: 'flex-start', // Align text to the left
        textAlign: 'left', // Text alignment
        border: isActive
          ? `1px solid ${jbColors.support.alertBlue[110]}`
          : `1px solid ${jbColors.support.solidGrayIcon[10]}`,
      },
      box: {
        width: '1.5em', // Adjusted width
        height: '1.5em', // Adjusted height
        borderRadius: '50%', // Circular shape
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: hover || isActive ? jbColors.core.reflexBlue[100] : jbColors.support.solidGrayIcon[10],
      },
      grid: {
        display: 'flex',
        whiteSpace: 'nowrap',
        mb: 3,
        flexWrap: 'wrap',
      }, // Flex wrap added
    };
  }, [isActive, subText, hover]);

  return (
    <Box
      data-testid={`product-status-filter-${text.toLowerCase().replace(' ', '')}`}
      sx={styles.grid}
      onClick={(e) => handleClick(e, filterKey)}
    >
      {loading ? (
        <Skeleton variant="text" width={200} />
      ) : (
        <Button sx={styles.button} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <Box
            sx={{
              p: 1.5,
            }}
          >
            {text}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={styles.box}>
              <Typography sx={{ color: jbColors.core.stone[10] }}>{subText ?? '0'}</Typography>
            </Box>
          </Box>
        </Button>
      )}
    </Box>
  );
};
